/**
 * Leitor mínimo de arquivos .xlsx, sem dependência externa.
 *
 * O .xlsx é um ZIP: percorremos o End of Central Directory e o Central Directory
 * para localizar as entradas e descomprimimos apenas as necessárias com
 * DecompressionStream("deflate-raw"). O XML da planilha é lido com DOMParser.
 *
 * Só é usado para leitura local no navegador — nada é enviado para servidor.
 */

const ASSINATURA_EOCD = 0x06054b50;
const ASSINATURA_CENTRAL = 0x02014b50;
const ASSINATURA_LOCAL = 0x04034b50;

const NAMESPACE_RELACIONAMENTOS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

export interface LinhaPlanilha {
    numero: number;
    celulas: { [coluna: string]: string };
}

interface EntradaZip {
    nome: string;
    metodo: number;
    tamanhoComprimido: number;
    offsetLocal: number;
}

interface PacoteXlsx {
    tem: (nome: string) => boolean;
    nomes: () => string[];
    texto: (nome: string) => Promise<string>;
}

function decodificarTexto(bytes: Uint8Array): string {
    return new TextDecoder("utf-8").decode(bytes);
}

function lerEntradas(bytes: Uint8Array): EntradaZip[] {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const tamanho = bytes.byteLength;

    let eocd = -1;
    const limite = Math.max(0, tamanho - 66000);

    for (let i = tamanho - 22; i >= limite; i--) {
        if (view.getUint32(i, true) === ASSINATURA_EOCD) {
            eocd = i;
            break;
        }
    }

    if (eocd < 0) {
        throw new Error("O arquivo não parece ser um .xlsx válido (estrutura ZIP não encontrada).");
    }

    const totalEntradas = view.getUint16(eocd + 10, true);
    let ponteiro = view.getUint32(eocd + 16, true);

    if (ponteiro === 0xffffffff || totalEntradas === 0xffff) {
        throw new Error("Arquivo .xlsx no formato ZIP64 não é suportado nesta tela.");
    }

    const entradas: EntradaZip[] = [];

    for (let i = 0; i < totalEntradas; i++) {
        if (ponteiro + 46 > tamanho || view.getUint32(ponteiro, true) !== ASSINATURA_CENTRAL) {
            break;
        }

        const metodo = view.getUint16(ponteiro + 10, true);
        const tamanhoComprimido = view.getUint32(ponteiro + 20, true);
        const tamanhoNome = view.getUint16(ponteiro + 28, true);
        const tamanhoExtra = view.getUint16(ponteiro + 30, true);
        const tamanhoComentario = view.getUint16(ponteiro + 32, true);
        const offsetLocal = view.getUint32(ponteiro + 42, true);
        const nome = decodificarTexto(bytes.subarray(ponteiro + 46, ponteiro + 46 + tamanhoNome));

        entradas.push({ nome, metodo, tamanhoComprimido, offsetLocal });
        ponteiro += 46 + tamanhoNome + tamanhoExtra + tamanhoComentario;
    }

    return entradas;
}

function dadosComprimidos(bytes: Uint8Array, entrada: EntradaZip): Uint8Array {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const offset = entrada.offsetLocal;

    if (view.getUint32(offset, true) !== ASSINATURA_LOCAL) {
        throw new Error(`Entrada corrompida no .xlsx: ${entrada.nome}.`);
    }

    const tamanhoNome = view.getUint16(offset + 26, true);
    const tamanhoExtra = view.getUint16(offset + 28, true);
    const inicio = offset + 30 + tamanhoNome + tamanhoExtra;

    return bytes.subarray(inicio, inicio + entrada.tamanhoComprimido);
}

async function inflar(dados: Uint8Array, metodo: number): Promise<Uint8Array> {
    if (metodo === 0) {
        return dados.slice();
    }

    if (metodo !== 8) {
        throw new Error(`Compressão não suportada no .xlsx (método ${metodo}).`);
    }

    // O lib.dom desta versão do TypeScript ainda não descreve DecompressionStream.
    const Descompressor = (window as any).DecompressionStream;

    if (!Descompressor) {
        throw new Error(
            "Este navegador não consegue descompactar o .xlsx. Utilize o Google Chrome ou o Microsoft Edge atualizado."
        );
    }

    const origem = new ReadableStream({
        start(controlador: any) {
            controlador.enqueue(dados);
            controlador.close();
        },
    } as any);

    const leitor = (origem as any).pipeThrough(new Descompressor("deflate-raw")).getReader();
    const partes: Uint8Array[] = [];
    let total = 0;

    for (;;) {
        const passo = await leitor.read();

        if (passo.done) {
            break;
        }

        partes.push(passo.value);
        total += passo.value.length;
    }

    const saida = new Uint8Array(total);
    let posicao = 0;

    partes.forEach((parte) => {
        saida.set(parte, posicao);
        posicao += parte.length;
    });

    return saida;
}

async function abrirPacote(arquivo: File): Promise<PacoteXlsx> {
    const bytes = new Uint8Array(await arquivo.arrayBuffer());
    const porNome: { [nome: string]: EntradaZip } = {};

    lerEntradas(bytes).forEach((entrada) => {
        porNome[entrada.nome] = entrada;
    });

    return {
        tem: (nome: string) => !!porNome[nome],
        nomes: () => Object.keys(porNome),
        texto: async (nome: string) => {
            const entrada = porNome[nome];

            if (!entrada) {
                throw new Error(`Conteúdo ausente no .xlsx: ${nome}.`);
            }

            return decodificarTexto(await inflar(dadosComprimidos(bytes, entrada), entrada.metodo));
        },
    };
}

function parsearXml(texto: string): Document {
    const documento = new DOMParser().parseFromString(texto, "application/xml");

    if (documento.getElementsByTagName("parsererror").length > 0) {
        throw new Error("Não foi possível ler o conteúdo do .xlsx (XML inválido).");
    }

    return documento;
}

// getElementsByTagName resolve o caso comum (namespace padrão, sem prefixo);
// o segundo laço cobre planilhas geradas com prefixo (ex.: <x:row>).
function elementos(raiz: Document | Element, nome: string): Element[] {
    const lista: Element[] = [];
    const diretos = raiz.getElementsByTagName(nome);

    for (let i = 0; i < diretos.length; i++) {
        lista.push(diretos[i]);
    }

    if (lista.length > 0) {
        return lista;
    }

    const todos = raiz.getElementsByTagName("*");

    for (let i = 0; i < todos.length; i++) {
        if (todos[i].localName === nome) {
            lista.push(todos[i]);
        }
    }

    return lista;
}

function textoConcatenado(raiz: Element): string {
    const partes = elementos(raiz, "t");
    let valor = "";

    for (let i = 0; i < partes.length; i++) {
        valor += partes[i].textContent ?? "";
    }

    return valor;
}

async function lerSharedStrings(pacote: PacoteXlsx): Promise<string[]> {
    if (!pacote.tem("xl/sharedStrings.xml")) {
        return [];
    }

    const itens = elementos(parsearXml(await pacote.texto("xl/sharedStrings.xml")), "si");
    const valores: string[] = [];

    for (let i = 0; i < itens.length; i++) {
        valores.push(textoConcatenado(itens[i]));
    }

    return valores;
}

function normalizarCaminho(alvo: string): string {
    if (alvo.indexOf("/") === 0) {
        return alvo.substring(1);
    }

    if (alvo.indexOf("../") === 0) {
        return alvo.substring(3);
    }

    return `xl/${alvo}`;
}

async function caminhoPrimeiraPlanilha(pacote: PacoteXlsx): Promise<string> {
    if (pacote.tem("xl/workbook.xml") && pacote.tem("xl/_rels/workbook.xml.rels")) {
        const planilhas = elementos(parsearXml(await pacote.texto("xl/workbook.xml")), "sheet");

        if (planilhas.length > 0) {
            const identificador =
                planilhas[0].getAttributeNS(NAMESPACE_RELACIONAMENTOS, "id") || planilhas[0].getAttribute("r:id");

            if (identificador) {
                const relacoes = elementos(parsearXml(await pacote.texto("xl/_rels/workbook.xml.rels")), "Relationship");

                for (let i = 0; i < relacoes.length; i++) {
                    if (relacoes[i].getAttribute("Id") === identificador) {
                        const caminho = normalizarCaminho(relacoes[i].getAttribute("Target") ?? "");

                        if (pacote.tem(caminho)) {
                            return caminho;
                        }
                    }
                }
            }
        }
    }

    if (pacote.tem("xl/worksheets/sheet1.xml")) {
        return "xl/worksheets/sheet1.xml";
    }

    const primeira = pacote
        .nomes()
        .filter((nome) => nome.indexOf("xl/worksheets/") === 0 && nome.slice(-4) === ".xml")
        .sort()[0];

    if (!primeira) {
        throw new Error("Nenhuma planilha encontrada dentro do .xlsx.");
    }

    return primeira;
}

function colunaDaCelula(referencia: string): string {
    return referencia.replace(/[^A-Za-z]/g, "").toUpperCase();
}

function proximaColuna(coluna: string): string {
    if (!coluna) {
        return "A";
    }

    const letras = coluna.split("");
    let posicao = letras.length - 1;

    while (posicao >= 0) {
        if (letras[posicao] === "Z") {
            letras[posicao] = "A";
            posicao--;
        } else {
            letras[posicao] = String.fromCharCode(letras[posicao].charCodeAt(0) + 1);
            return letras.join("");
        }
    }

    return `A${letras.join("")}`;
}

function valorDaCelula(celula: Element, compartilhadas: string[]): string {
    const tipo = celula.getAttribute("t");

    if (tipo === "inlineStr") {
        return textoConcatenado(celula);
    }

    const valores = elementos(celula, "v");

    if (valores.length === 0) {
        return "";
    }

    const bruto = valores[0].textContent ?? "";

    if (tipo === "s") {
        const indice = parseInt(bruto, 10);
        return isNaN(indice) ? "" : compartilhadas[indice] ?? "";
    }

    return bruto;
}

/**
 * Lê a primeira planilha do arquivo e devolve as linhas presentes no XML.
 * As colunas são esparsas: a chave de `celulas` é sempre a letra da coluna
 * (obtida do atributo `r` da célula), nunca a posição do elemento.
 */
export async function lerPrimeiraPlanilha(arquivo: File): Promise<LinhaPlanilha[]> {
    const pacote = await abrirPacote(arquivo);
    const compartilhadas = await lerSharedStrings(pacote);
    const planilha = parsearXml(await pacote.texto(await caminhoPrimeiraPlanilha(pacote)));

    const linhasXml = elementos(planilha, "row");
    const linhas: LinhaPlanilha[] = [];

    for (let i = 0; i < linhasXml.length; i++) {
        const numeroBruto = parseInt(linhasXml[i].getAttribute("r") ?? "", 10);
        const numero = isNaN(numeroBruto) ? i + 1 : numeroBruto;
        const celulasXml = elementos(linhasXml[i], "c");
        const celulas: { [coluna: string]: string } = {};

        let ultimaColuna = "";

        for (let j = 0; j < celulasXml.length; j++) {
            const referencia = celulasXml[j].getAttribute("r") ?? "";
            const coluna = referencia ? colunaDaCelula(referencia) : proximaColuna(ultimaColuna);

            ultimaColuna = coluna;
            celulas[coluna] = valorDaCelula(celulasXml[j], compartilhadas);
        }

        linhas.push({ numero, celulas });
    }

    return linhas;
}
